<template>
  <div>
    <!-- ============================================================
         Dialog Sửa giá khoản phụ (Principal Override)
         Sử dụng: <principal-override-edit-dialog ref="overrideEditDialog" @saved="onSaved" />
         Mở:     this.$refs.overrideEditDialog.open(idRevenueStudent, overrides)
         ============================================================ -->
    <dialog-base
      :visible.sync="visible"
      title="Sửa giá khoản phụ"
      size="lg"
      cancel-text="Hủy"
      confirm-text="Lưu lại"
      :confirm-loading="loading"
      :use-panel="false"
      top="6vh"
      @confirm="handleSubmit"
      @cancel="handleClose"
    >
      <div class="poe-content">
        <!-- Hướng dẫn -->
        <div class="poe-tip mb-4">
          <i class="el-icon-info poe-tip-icon" />
          <span class="poe-tip-text">
            Điều chỉnh đơn giá và chính sách giảm giá riêng cho từng khoản phụ trong công thức tính. Dữ liệu gốc hiển thị bên cạnh để đối chiếu.
          </span>
        </div>

        <div class="poe-decimal-toggle mb-4">
          <span>Cho phép số thập phân</span>
          <el-switch
            :value="allowDecimal"
            active-text="Có"
            inactive-text="Không"
            @change="handleAllowDecimalChange"
          />
        </div>

        <!-- Form cho từng khoản phụ -->
        <el-form
          ref="overrideForm"
          :model="formData"
          :rules="formRules"
          label-width="0"
          class="poe-form"
        >
          <div
            v-for="(item, index) in formData.overrides"
            :key="item.id || index"
            class="poe-item"
          >
            <!-- Header khoản phụ -->
            <div class="poe-item-head">
              <span class="poe-alias">{{ item.alias }}</span>
              <span class="poe-name">{{ item.principalName }}</span>
              <el-tag class="poe-original-tag ml-2">
                Gốc: {{ formatMoney(item.originalPrice) }} x {{ formatQuantity(item.originalQuantity) }} {{ item.originalUnit }}
              </el-tag>
            </div>

            <!-- Body: các trường chỉnh sửa -->
            <el-row :gutter="16" class="poe-item-body">
              <!-- Đơn giá -->
              <el-col :span="item.discount ? 5 : 8">
                <el-form-item
                  :prop="'overrides.' + index + '.price'"
                  :rules="priceRules"
                  label-width="0"
                >
                  <div class="poe-field-label">
                    <i class="el-icon-money mr-1" />Đơn giá (VNĐ)
                  </div>
                  <ElCurrencyInput
                    v-model="item.price"
                    :options="{ locale: 'vi-VN', precision: 0, autoDecimalMode: false, distractionFree: false }"
                    class="sk-u-w-full"
                  />
                </el-form-item>
              </el-col>

              <!-- Số lượng -->
              <el-col :span="item.discount ? 3 : 5">
                <el-form-item
                  :prop="'overrides.' + index + '.quantity'"
                  :rules="quantityRules"
                  label-width="0"
                >
                  <div class="poe-field-label">
                    <i class="el-icon-pie-chart mr-1" />Số lượng
                  </div>
                  <el-input
                    v-model.number="item.quantity"
                    type="number"
                    :step="allowDecimal ? 0.01 : 1"
                    class="sk-u-w-full"
                  />
                </el-form-item>
              </el-col>

              <!-- Đơn vị -->
              <el-col :span="item.discount ? 4 : 6">
                <el-form-item label-width="0">
                  <div class="poe-field-label">
                    <i class="el-icon-box mr-1" />Đơn vị
                  </div>
                  <el-select
                    v-model="item.unit"
                    filterable
                    allow-create
                    default-first-option
                    placeholder="Đơn vị"
                    class="sk-u-w-full"
                  >
                    <el-option v-for="u in unitsList" :key="u" :label="u" :value="u" />
                  </el-select>
                </el-form-item>
              </el-col>

              <!-- Giảm giá toggle -->
              <el-col :span="item.discount ? 4 : 5">
                <el-form-item label-width="0">
                  <div class="poe-field-label">
                    <i class="el-icon-price-tag mr-1" />Giảm giá
                  </div>
                  <div class="poe-switch-wrapper">
                    <el-switch
                      v-model="item.discount"
                      active-text="Có"
                      inactive-text="Không"
                      active-color="#10b981"
                      inactive-color="#cbd5e1"
                      @change="val => onDiscountToggle(index, val)"
                    />
                  </div>
                </el-form-item>
              </el-col>

              <!-- Loại giảm giá -->
              <el-col v-if="item.discount" :span="4">
                <el-form-item label-width="0">
                  <div class="poe-field-label">Loại giảm</div>
                  <el-select
                    v-model="item.discountType"
                    placeholder="Chọn"
                    class="sk-u-w-full"
                  >
                    <el-option label="%" value="PERCENT" />
                    <el-option label="VNĐ" value="MONEY" />
                  </el-select>
                </el-form-item>
              </el-col>

              <!-- Giá trị giảm -->
              <el-col v-if="item.discount" :span="4">
                <el-form-item label-width="0">
                  <div class="poe-field-label">
                    {{ item.discountType === 'PERCENT' ? 'Phần trăm (%)' : 'Số tiền' }}
                  </div>
                  <template v-if="item.discountType === 'MONEY'">
                    <ElCurrencyInput
                      v-model="item.discountNumber"
                      :options="{ locale: 'vi-VN', precision: 0, autoDecimalMode: false, distractionFree: false }"
                      class="sk-u-w-full"
                    />
                  </template>
                  <template v-else>
                    <el-input-number v-discount-percent
                      v-model="item.discountNumber"
                      :min="0"
                      :max="100"
                      :precision="0"
                      controls-position="right"
                      class="sk-u-w-full"
                    />
                  </template>
                </el-form-item>
              </el-col>
            </el-row>

            <!-- Dải tóm tắt tiền tệ cho từng khoản phụ -->
            <div class="poe-item-summary-bar mt-3">
              <div class="poe-summary-item">
                <span class="poe-summary-label">Thành tiền gốc:</span>
                <span class="poe-summary-value">{{ formatMoney(getItemOriginalTotal(item)) }}</span>
              </div>
              <div v-if="item.discount" class="poe-summary-item text-danger">
                <span class="poe-summary-label">Số tiền được giảm:</span>
                <span class="poe-summary-value">-{{ formatMoney(getItemDiscountAmount(item)) }}</span>
              </div>
              <div class="poe-summary-item poe-summary-item--final">
                <span class="poe-summary-label">Tổng sau giảm:</span>
                <span class="poe-summary-value">{{ formatMoney(getItemFinalTotal(item)) }}</span>
              </div>
            </div>
          </div>
        </el-form>
      </div>
    </dialog-base>
  </div>
</template>

<script>
import { discountPercentInput, hasOverLimitPercentDiscount, DISCOUNT_PERCENT_LIMIT_MESSAGE } from "@/utils/discountPercent";
import DialogBase from "@/components/DialogBase.vue";
import ElCurrencyInput from "@/components/ElCurrencyInput.vue";
import managingRevenueService from "@/services/tutition_v3/ManagingRevenueService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import { DEFAULT_UNITS } from "@/constant/tuitionConstant";
import {
  formatQuantity as formatQuantityValue,
  hasQuantityFraction,
  isQuantityPrecisionValid,
  toQuantityJsonNumber,
} from "@/components/tutition_v3/utils/quantity";

export default {
  directives: { discountPercent: discountPercentInput },
  name: "PrincipalOverrideEditDialog",

  components: { DialogBase, ElCurrencyInput },

  data() {
    const validateQuantity = (rule, value, callback) => {
      if (!isQuantityPrecisionValid(value, this.allowDecimal)) {
        callback(new Error(this.allowDecimal
          ? "Tối đa 2 chữ số thập phân"
          : "Số lượng phải là số nguyên"));
        return;
      }
      callback();
    };

    return {
      visible: false,
      loading: false,
      allowDecimal: false,
      idRevenueStudent: null,
      unitsList: [...DEFAULT_UNITS],
      formData: {
        overrides: [],
      },
      formRules: {},
      priceRules: [
        { required: true, message: "Vui lòng nhập đơn giá", trigger: "blur" },
        { type: "number", min: 0, message: "Đơn giá phải >= 0", trigger: "blur" },
      ],
      quantityRules: [
        { required: true, message: "Vui lòng nhập số lượng", trigger: "blur" },
        { validator: validateQuantity, trigger: ["blur", "change"] },
      ],
    };
  },

  methods: {
    // ─── Public API ──────────────────────────────────────────────

    /**
     * Mở dialog
     * @param {String} idRevenueStudent - UUID bản đăng ký khoản thu
     * @param {Array} overrides - Danh sách override hiện tại
     */
    open(idRevenueStudent, overrides) {
      this.idRevenueStudent = idRevenueStudent;

      // Deep clone để tránh mutate data gốc và đảm bảo Vue reactivity clean
      const cloned = JSON.parse(JSON.stringify(overrides || []));
      this.formData.overrides = cloned.map((item) => ({
        id: item.id,
        idPrincipalRevenue: item.idPrincipalRevenue,
        principalCode: item.principalCode,
        principalName: item.principalName,
        alias: item.alias,
        price: item.price ?? 0,
        quantity: item.quantity === undefined ? 1 : item.quantity,
        unit: item.unit || "",
        discount: item.discount ?? false,
        discountType: item.discountType ?? null,
        discountNumber: item.discountNumber ?? null,
        originalPrice: item.originalPrice,
        originalQuantity: item.originalQuantity,
        originalUnit: item.originalUnit,
        // Backup để tránh mất giá trị do form reactivity
        _backupDiscountType: item.discountType ?? null,
        _backupDiscountNumber: item.discountNumber ?? null,
      }));
      this.allowDecimal = this.formData.overrides.some(item => hasQuantityFraction(item.quantity));

      // Hiển thị dialog sau khi data đã sẵn sàng
      this.$nextTick(() => {
        this.visible = true;
        this.$nextTick(() => {
          if (this.$refs.overrideForm) {
            this.$refs.overrideForm.clearValidate();
          }
        });
      });
    },

    handleClose() {
      this.visible = false;
      this.formData.overrides = [];
      this.$nextTick(() => this.$refs.overrideForm?.clearValidate());
    },

    // ─── Submit ───────────────────────────────────────────────────

    handleSubmit() {
      this.$refs.overrideForm.validate(async (valid) => {
        if (!valid) return;

        // Validate discount fields thủ công (tránh issue với el-form-item + v-if)
        for (const item of this.formData.overrides) {
          if (item.discount) {
            if (!item.discountType) {
              NotifyMessage.leftCornerNotificationError(
                `Khoản "${item.principalName}": vui lòng chọn loại giảm giá`
              );
              return;
            }
            if (item.discountNumber === null || item.discountNumber === undefined || item.discountNumber === "") {
              NotifyMessage.leftCornerNotificationError(
                `Khoản "${item.principalName}": vui lòng nhập giá trị giảm`
              );
              return;
            }
            const discountNum = Number(item.discountNumber);
            if (isNaN(discountNum) || discountNum < 0) {
              NotifyMessage.leftCornerNotificationError(
                `Khoản "${item.principalName}": giá trị giảm phải >= 0`
              );
              return;
            }
            if (item.discountType === "PERCENT" && discountNum > 100) {
              NotifyMessage.leftCornerNotificationError(
                `Khoản "${item.principalName}": phần trăm giảm phải trong khoảng 0-100%`
              );
              return;
            }
          }
        }

        this.loading = true;
        try {
          const payload = {
            idRevenueStudent: this.idRevenueStudent,
            overrides: this.formData.overrides.map((item) => ({
              id: item.id,
              idPrincipalRevenue: item.idPrincipalRevenue,
              principalCode: item.principalCode,
              principalName: item.principalName,
              alias: item.alias,
              price: item.price,
              quantity: toQuantityJsonNumber(item.quantity),
              unit: item.unit,
              discount: item.discount,
              discountType: item.discount ? item.discountType : null,
              discountNumber: item.discount ? item.discountNumber : null,
              originalPrice: item.originalPrice,
              originalQuantity: toQuantityJsonNumber(item.originalQuantity),
              originalUnit: item.originalUnit,
            })),
          };

          if (hasOverLimitPercentDiscount(payload)) {
            NotifyMessage.leftCornerNotificationError(DISCOUNT_PERCENT_LIMIT_MESSAGE);
            return;
          }

          const res = await managingRevenueService.updatePrincipalOverrides(payload);
          const responseData = res.data;

          if (responseData?.status === 200) {
            NotifyMessage.leftCornerNotificationSuccess("Cập nhật khoản phụ thành công!");
            this.visible = false;
            this.$emit("saved", responseData.data);
          } else {
            NotifyMessage.leftCornerNotificationError(
              responseData?.message || "Cập nhật thất bại!"
            );
          }
        } catch (err) {
          NotifyMessage.leftCornerNotificationError(
            err?.response?.data?.message || "Cập nhật thất bại!"
          );
        } finally {
          this.loading = false;
        }
      });
    },

    // ─── Discount Helpers ─────────────────────────────────────────

    onDiscountToggle(index, val) {
      const item = this.formData.overrides[index];
      if (!val) {
        this.$set(item, 'discountType', null);
        this.$set(item, 'discountNumber', null);
      } else {
        // Restore from backup or set defaults
        this.$set(item, 'discountType', item._backupDiscountType || 'PERCENT');
        this.$set(item, 'discountNumber', item._backupDiscountNumber ?? 0);
      }
      this.$nextTick(() => this.$refs.overrideForm?.clearValidate());
    },

    handleAllowDecimalChange(value) {
      if (!value && this.formData.overrides.some(item => hasQuantityFraction(item.quantity))) {
        NotifyMessage.leftCornerNotificationWarning(
          "Vui lòng sửa tất cả số lượng về số nguyên trước khi tắt chế độ thập phân."
        );
        return;
      }
      this.allowDecimal = value;
      this.$nextTick(() => this.$refs.overrideForm?.clearValidate());
    },

    discountTypeRules(index) {
      const item = this.formData.overrides[index];
      if (!item || !item.discount) return [];
      return [{ required: true, message: "Chọn loại giảm", trigger: "change" }];
    },

    discountNumberRules(index) {
      const item = this.formData.overrides[index];
      if (!item || !item.discount) return [];
      const rules = [
        { required: true, message: "Nhập giá trị giảm", trigger: "blur" },
      ];
      if (item.discountType === "PERCENT") {
        rules.push({
          type: "number",
          min: 0,
          max: 100,
          message: "Phần trăm từ 0–100",
          trigger: "blur",
        });
      } else {
        rules.push({
          type: "number",
          min: 0,
          message: "Giá trị >= 0",
          trigger: "blur",
        });
      }
      return rules;
    },

    // ─── Calculation helpers ──────────────────────────────────────

    getItemOriginalTotal(item) {
      const price = Number(item.price) || 0;
      const qty = Number(item.quantity) || 0;
      return price * qty;
    },

    getItemDiscountAmount(item) {
      if (!item.discount) return 0;
      const originalTotal = this.getItemOriginalTotal(item);
      const discountNum = Number(item.discountNumber) || 0;
      if (item.discountType === "PERCENT") {
        return (originalTotal * discountNum) / 100;
      }
      if (item.discountType === "MONEY") {
        return discountNum;
      }
      return 0;
    },

    getItemFinalTotal(item) {
      const originalTotal = this.getItemOriginalTotal(item);
      const discountAmt = this.getItemDiscountAmount(item);
      return Math.max(0, originalTotal - discountAmt);
    },

    // ─── Format ───────────────────────────────────────────────────

    formatMoney(value) {
      if (value === null || value === undefined || value === "") return "—";
      return Number(value).toLocaleString("vi-VN") + " ₫";
    },

    formatQuantity(value) {
      return formatQuantityValue(value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-dialog";

/deep/ .el-input-number.sk-u-w-full { width: 100%; }
/deep/ .el-select.sk-u-w-full       { width: 100%; }

.poe-content {
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 4px;
}

/* Tip instruction callout */
.poe-tip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background-color: #f0f7ff;
  border: 1px solid #bfdbfe;
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  text-align: left;
}
.poe-tip-icon {
  font-size: 16px;
  color: #3b82f6;
}
.poe-tip-text {
  font-size: 13px;
  font-weight: 500;
  color: #1e3a8a;
  line-height: 1.4;
}

.poe-decimal-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

/* Mỗi khoản phụ */
.poe-item {
  padding: 20px;
  margin-bottom: 20px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #6366f1; /* Indigo left border */
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.05);
  transition: all 0.2s ease-in-out;
  text-align: left;

  &:hover {
    border-color: #818cf8;
    box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.08);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

/* Header khoản phụ */
.poe-item-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.poe-alias {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: #e0f2fe;
  border: 1px solid #bae6fd;
  border-radius: 50%;
  font-weight: 700;
  font-size: 12px;
  color: #0369a1;
  flex-shrink: 0;
}

.poe-name {
  font-weight: 700;
  font-size: 14.5px;
  color: #0f172a;
}

.poe-original-tag {
  background-color: #f1f5f9 !important;
  border-color: #e2e8f0 !important;
  color: #475569 !important;
  font-weight: 500;
  font-size: 11px;
  border-radius: 6px !important;
}

/* Body */
.poe-item-body {
  /* spacing handled by el-row gutter */
}

/* Field label */
.poe-field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.poe-switch-wrapper {
  height: 36px;
  display: flex;
  align-items: center;
}

/* Custom styled inputs inside elements */
/deep/ .el-input__inner {
  border-radius: 8px !important;
}

/* Tóm tắt tiền tệ cho từng khoản phụ */
.poe-item-summary-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
  padding-top: 14px;
  border-top: 1px dashed #e2e8f0;
  margin-top: 14px;
  flex-wrap: wrap;
}

.poe-summary-item {
  font-size: 13px;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &.text-danger {
    color: #ef4444;
    font-weight: 600;
  }
}

.poe-summary-label {
  color: #64748b;
}

.poe-summary-value {
  font-weight: 700;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
}

.poe-summary-item--final {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 4px 10px;
  border-radius: 6px;
  
  .poe-summary-label {
    color: #1e40af;
    font-weight: 600;
  }
  .poe-summary-value {
    color: #1d4ed8;
    font-size: 14px;
  }
}

/* Dialog base width overrides - make it responsive but wide up to 1050px */
/deep/ .el-dialog {
  max-width: 1050px !important;
  width: 90% !important;
}

/* Dialog footer buttons override */
/deep/ .el-dialog__footer .el-button--default {
  background: #fff5f5 !important;
  border: 1px solid #fecaca !important;
  color: #dc2626 !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  
  &:hover {
    background: #fee2e2 !important;
    color: #b91c1c !important;
    border-color: #fca5a5 !important;
  }
}

/deep/ .el-dialog__footer .el-button--primary {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.2) !important;
  
  &:hover {
    box-shadow: 0 4px 12px -1px rgba(16, 185, 129, 0.3) !important;
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
}
</style>
